import { baseApi } from '../baseApi';

export interface ExploreCategory {
  id: string;
  tenantId: string;
  title: string;
  slug: string;
  tagLine?: string | null;
  description?: string | null;
  categoryType: 'DRIVE' | 'DICTIONARY' | 'DRAVYA' | 'CHIKITSA' | 'CUSTOM';
  icon?: string;
  color?: string;
  bgColor?: string;
  badgeText?: string | null;
  freeLimit?: number;
  isLockedByDefault?: boolean;
  displayOrder?: number;
  isActive?: boolean;
  itemCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExploreDriveResource {
  id: string;
  tenantId: string;
  title: string;
  subjectName: string;
  yearName: string;
  yearKey: string;
  academicYearId?: string | null;
  resourceType: string;
  fileCountOrSize: string;
  tagLine?: string | null;
  description: string;
  driveUrl: string;
  color?: string;
  icon?: string;
  isPopular?: boolean;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExploreDictionaryTerm {
  id: string;
  tenantId: string;
  termSanskrit: string;
  termEnglish: string;
  phonetic?: string | null;
  category: string;
  definition: string;
  classicalReference?: string | null;
  shloka?: string | null;
  firstLetter: string;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExploreDravyagunaHerb {
  id: string;
  tenantId: string;
  // Header & Basic Info
  sanskritName: string;
  imageUrl?: string | null;
  botanicalName: string;
  family: string;
  pradhanKarma?: string | null;
  partUsed: string;
  synonyms?: string | null;
  commonNames?: string | null;

  // Section 1: Rasa Panchaka & Karma
  rasa: string;
  guna: string;
  virya: string;
  vipaka: string;
  prabhava?: string | null;
  doshaKarma?: string | null;
  generalKarma?: string | null;
  karma: string;

  // Section 2: Morphology
  habit?: string | null;
  morphologyRoot?: string | null;
  morphologyStem?: string | null;
  morphologyLeaf?: string | null;
  morphologyFlower?: string | null;
  inflorescence?: string | null;
  morphologyFruit?: string | null;
  morphologySeed?: string | null;

  // Section 3: Therapeutic Uses
  majorIndications: string;
  amayikPrayog1?: string | null;
  amayikPrayog2?: string | null;
  amayikPrayog3?: string | null;
  classicalDosage: string;
  phytoConstituents?: string | null;
  vishishtYoga?: string | null;
  majorFormulations?: string | null;

  isLocked?: boolean;
  isPopular?: boolean;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExploreChikitsaProtocol {
  id: string;
  tenantId: string;
  // Basic & Diagnostic
  diseaseNameSanskrit: string;
  diseaseNameEnglish: string;
  diagramUrl?: string | null;
  parichay?: string | null;
  paribhasha?: string | null;
  doshaPredominance: string;
  dushya?: string | null;
  sampraptiSummary: string;

  // Treatment & Formulations
  chikitsaSutra: string;
  visheshChikitsa?: string | null;
  shamanaAushadhi: string;
  dosageDetails?: string | null;
  anupan?: string | null;
  sevanKala?: string | null;
  shodhanaTherapy?: string | null;
  pathya: string;
  apathya: string;
  modernTreatment?: string | null;
  referenceSamhita: string;

  isLocked?: boolean;
  isPopular?: boolean;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExploreSubmission {
  id: string;
  tenantId: string;
  userId?: string | null;
  userName: string;
  userEmail: string;
  resourceType: string;
  academicYear: string;
  subjectName: string;
  title: string;
  linkUrl: string;
  notesRemarks?: string | null;
  reviewStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  adminNotes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const exploreApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ----------------------------------------------------
    // 1. DRIVE RESOURCES
    // ----------------------------------------------------
    getDriveResources: builder.query<{ success: boolean; count: number; data: ExploreDriveResource[] }, {
      yearKey?: string;
      resourceType?: string;
      search?: string;
      includeInactive?: boolean;
    } | void>({
      query: (params) => ({
        url: '/api/explore/drive-resources',
        params: params || { includeInactive: true },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ExploreDriveResource' as const, id })),
              { type: 'ExploreDriveResource', id: 'LIST' },
            ]
          : [{ type: 'ExploreDriveResource', id: 'LIST' }],
    }),

    createDriveResource: builder.mutation<{ success: boolean; message: string; data: ExploreDriveResource }, Partial<ExploreDriveResource>>({
      query: (body) => ({
        url: '/api/explore/admin/drive-resources',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ExploreDriveResource', id: 'LIST' }],
    }),

    updateDriveResource: builder.mutation<{ success: boolean; message: string; data: ExploreDriveResource }, { id: string; [key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/api/explore/admin/drive-resources/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ExploreDriveResource', id },
        { type: 'ExploreDriveResource', id: 'LIST' },
      ],
    }),

    deleteDriveResource: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/api/explore/admin/drive-resources/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ExploreDriveResource', id: 'LIST' }],
    }),

    // ----------------------------------------------------
    // 2. DICTIONARY TERMS
    // ----------------------------------------------------
    getDictionaryTerms: builder.query<{ success: boolean; count: number; data: ExploreDictionaryTerm[] }, {
      letter?: string;
      search?: string;
      category?: string;
      includeInactive?: boolean;
    } | void>({
      query: (params) => ({
        url: '/api/explore/dictionary',
        params: params || { includeInactive: true },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ExploreDictionaryTerm' as const, id })),
              { type: 'ExploreDictionaryTerm', id: 'LIST' },
            ]
          : [{ type: 'ExploreDictionaryTerm', id: 'LIST' }],
    }),

    createDictionaryTerm: builder.mutation<{ success: boolean; message: string; data: ExploreDictionaryTerm }, Partial<ExploreDictionaryTerm>>({
      query: (body) => ({
        url: '/api/explore/admin/dictionary',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ExploreDictionaryTerm', id: 'LIST' }],
    }),

    updateDictionaryTerm: builder.mutation<{ success: boolean; message: string; data: ExploreDictionaryTerm }, { id: string; [key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/api/explore/admin/dictionary/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ExploreDictionaryTerm', id },
        { type: 'ExploreDictionaryTerm', id: 'LIST' },
      ],
    }),

    deleteDictionaryTerm: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/api/explore/admin/dictionary/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ExploreDictionaryTerm', id: 'LIST' }],
    }),

    // ----------------------------------------------------
    // 3. DRAVYAGUNA HERBS
    // ----------------------------------------------------
    getDravyagunaHerbs: builder.query<{ success: boolean; count: number; data: ExploreDravyagunaHerb[] }, {
      search?: string;
      rasa?: string;
      virya?: string;
      vipaka?: string;
      includeInactive?: boolean;
    } | void>({
      query: (params) => ({
        url: '/api/explore/dravyaguna',
        params: params || { includeInactive: true },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ExploreDravyagunaHerb' as const, id })),
              { type: 'ExploreDravyagunaHerb', id: 'LIST' },
            ]
          : [{ type: 'ExploreDravyagunaHerb', id: 'LIST' }],
    }),

    createDravyagunaHerb: builder.mutation<{ success: boolean; message: string; data: ExploreDravyagunaHerb }, Partial<ExploreDravyagunaHerb>>({
      query: (body) => ({
        url: '/api/explore/admin/dravyaguna',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ExploreDravyagunaHerb', id: 'LIST' }],
    }),

    updateDravyagunaHerb: builder.mutation<{ success: boolean; message: string; data: ExploreDravyagunaHerb }, { id: string; [key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/api/explore/admin/dravyaguna/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ExploreDravyagunaHerb', id },
        { type: 'ExploreDravyagunaHerb', id: 'LIST' },
      ],
    }),

    deleteDravyagunaHerb: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/api/explore/admin/dravyaguna/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ExploreDravyagunaHerb', id: 'LIST' }],
    }),

    // ----------------------------------------------------
    // 4. CHIKITSA PROTOCOLS
    // ----------------------------------------------------
    getChikitsaProtocols: builder.query<{ success: boolean; count: number; data: ExploreChikitsaProtocol[] }, {
      search?: string;
      dosha?: string;
      includeInactive?: boolean;
    } | void>({
      query: (params) => ({
        url: '/api/explore/chikitsa',
        params: params || { includeInactive: true },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ExploreChikitsaProtocol' as const, id })),
              { type: 'ExploreChikitsaProtocol', id: 'LIST' },
            ]
          : [{ type: 'ExploreChikitsaProtocol', id: 'LIST' }],
    }),

    createChikitsaProtocol: builder.mutation<{ success: boolean; message: string; data: ExploreChikitsaProtocol }, Partial<ExploreChikitsaProtocol>>({
      query: (body) => ({
        url: '/api/explore/admin/chikitsa',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ExploreChikitsaProtocol', id: 'LIST' }],
    }),

    updateChikitsaProtocol: builder.mutation<{ success: boolean; message: string; data: ExploreChikitsaProtocol }, { id: string; [key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/api/explore/admin/chikitsa/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ExploreChikitsaProtocol', id },
        { type: 'ExploreChikitsaProtocol', id: 'LIST' },
      ],
    }),

    deleteChikitsaProtocol: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/api/explore/admin/chikitsa/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ExploreChikitsaProtocol', id: 'LIST' }],
    }),

    // ----------------------------------------------------
    // 5. SUBMISSIONS REVIEW
    // ----------------------------------------------------
    getExploreSubmissions: builder.query<{ success: boolean; count: number; data: ExploreSubmission[] }, {
      status?: string;
      search?: string;
    } | void>({
      query: (params) => ({
        url: '/api/explore/admin/submissions',
        params: params || {},
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ExploreSubmission' as const, id })),
              { type: 'ExploreSubmission', id: 'LIST' },
            ]
          : [{ type: 'ExploreSubmission', id: 'LIST' }],
    }),

    reviewExploreSubmission: builder.mutation<{
      success: boolean;
      message: string;
      data: ExploreSubmission;
      convertedResource?: ExploreDriveResource | null;
    }, {
      id: string;
      reviewStatus: 'APPROVED' | 'REJECTED' | 'PENDING';
      adminNotes?: string;
      convertToResource?: boolean;
      yearKey?: string;
      fileCountOrSize?: string;
      color?: string;
      icon?: string;
    }>({
      query: ({ id, ...body }) => ({
        url: `/api/explore/admin/submissions/${id}/review`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [
        { type: 'ExploreSubmission', id: 'LIST' },
        { type: 'ExploreDriveResource', id: 'LIST' },
      ],
    }),

    // ----------------------------------------------------
    // 6. DYNAMIC CATEGORIES
    // ----------------------------------------------------
    getExploreCategories: builder.query<{ success: boolean; count: number; data: ExploreCategory[] }, { includeInactive?: boolean } | void>({
      query: (params) => ({
        url: '/api/explore/categories',
        params: params || { includeInactive: true },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ExploreCategory' as const, id })),
              { type: 'ExploreCategory', id: 'LIST' },
            ]
          : [{ type: 'ExploreCategory', id: 'LIST' }],
    }),

    createExploreCategory: builder.mutation<{ success: boolean; message: string; data: ExploreCategory }, Partial<ExploreCategory>>({
      query: (body) => ({
        url: '/api/explore/admin/categories',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ExploreCategory', id: 'LIST' }],
    }),

    updateExploreCategory: builder.mutation<{ success: boolean; message: string; data: ExploreCategory }, { id: string; [key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/api/explore/admin/categories/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ExploreCategory', id },
        { type: 'ExploreCategory', id: 'LIST' },
      ],
    }),

    deleteExploreCategory: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/api/explore/admin/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ExploreCategory', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetDriveResourcesQuery,
  useCreateDriveResourceMutation,
  useUpdateDriveResourceMutation,
  useDeleteDriveResourceMutation,
  useGetDictionaryTermsQuery,
  useCreateDictionaryTermMutation,
  useUpdateDictionaryTermMutation,
  useDeleteDictionaryTermMutation,
  useGetDravyagunaHerbsQuery,
  useCreateDravyagunaHerbMutation,
  useUpdateDravyagunaHerbMutation,
  useDeleteDravyagunaHerbMutation,
  useGetChikitsaProtocolsQuery,
  useCreateChikitsaProtocolMutation,
  useUpdateChikitsaProtocolMutation,
  useDeleteChikitsaProtocolMutation,
  useGetExploreSubmissionsQuery,
  useReviewExploreSubmissionMutation,
  useGetExploreCategoriesQuery,
  useCreateExploreCategoryMutation,
  useUpdateExploreCategoryMutation,
  useDeleteExploreCategoryMutation,
} = exploreApi;
